import { useEffect, useMemo, useState } from 'react';
import { Hexagon } from 'react-hexgrid';
import ionLogoWhite from '../../images/ion-plus-white.png';
import ionLogoBlack from '../../images/ion-plus-black.png';
import { HexGridData, HiveGenerationMode } from '../../shared/types/shared/hex-grid';
import { generateDataTiles, GetUKLayout, processData } from '../../shared/utils/StatusHiveUtils';
import { useGetAssetStatusQuery } from '../../shared/api/AssetApi';
import { IPortalRouteOptions } from "../../shared/types/shared/routes/routeTypes";
import { Hive } from '@mui/icons-material';
import { useToastAlertContext } from '../../components/ToastAlert';
import HexContainer from '../../components/shared/hex-grid/HexContainer';
import { useColourModeContext } from '../../components/ColourMode';
import { common } from '@mui/material/colors';
import { IHexGridConfig, IHiveDimensions, getCurrentDimension } from '../../shared/utils/HexGridUtils';
import { IDispatchRequest, IDispatchResponse, SiteManualStatus, StatusHiveDataTile, StatusHiveSiteData } from '../../shared/types/asset/status-hive';
import { DispatchButton } from '../../components/status-hive/DispatchButton';
import { usePortalContext } from '../../components/PortalWrapper';
import { UserPermissionCollection } from '../../shared/types/shared/auth/userDataTypes';
import { AssetPermissions } from '../../shared/types/shared/auth/permissionsTypes';
import { CancelDispatchButton } from '../../components/status-hive/CancelDispatchButton';
import { Typography } from '@mui/material';
import { IAdvancedModalOptions, useModalWindowContext } from '../../components/ModalWindow';
import { ISiteStatus } from '../../shared/types/ISiteStatus';
import piApplicationDispatchApi from '../../shared/api/DispatchApi';
import { useGetAttributeValuesQuery } from '../../shared/api/MetadataApi';
import { IMetadataAttributeValue } from '../../shared/types/system-admin/IMetadataAttributeValue';


const StatusHivePage: React.FC = () => {
    const { openModal } = useModalWindowContext();
    const { popToast } = useToastAlertContext();
    const { isDarkMode } = useColourModeContext();
    const [dispatchMode, setDispatchMode] = useState<boolean>(false)
    const [flashSync, setFlashSync] = useState<boolean>(false)
    const [disptachModeAuthenticated, setDispatchModeAuthenitcated] = useState<boolean>(false)
    const { currentUser } = usePortalContext();

    useEffect(() => { // Disable scroll on load
        document.body.style.overflow = 'hidden';

        // Reenable scroll on unload
        return () => {
            document.body.style.overflow = 'unset';
        }
    }, []);

    // Configure API calls
    const statusPollingInterval = useMemo(() => 60 * 1000, []);
    const { data: statusList, isSuccess: statusesSucceeded, isLoading: statusesLoading } = useGetAssetStatusQuery(undefined, { pollingInterval: statusPollingInterval });
    const { data: rawAttributeData, isSuccess: rawAttributesSucceeded, isLoading: rawAttributesLoading } = useGetAttributeValuesQuery();

    const [siteData, setSiteData] = useState<ISiteStatus[]>([]);
    const [data, setData] = useState<StatusHiveSiteData[]>([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => { // Track when loading is complete
        if (!statusesLoading && statusesSucceeded && !rawAttributesLoading && rawAttributesSucceeded) {
            setLoaded(true);
        }
    }, [rawAttributesLoading, rawAttributesSucceeded, statusesLoading, statusesSucceeded]);

    const gridConfig = useMemo((): IHexGridConfig => {
        return {
            heightOffset: -64,
        }
    }, []);

    const [dimensions, setDimensions] = useState<IHiveDimensions>(getCurrentDimension(gridConfig)); // Dimensions
    const [grid, setGrid] = useState<HexGridData>(new HexGridData(0, 0)); // All Grid Hexagons
    const [dataTiles, setDataTiles] = useState<StatusHiveDataTile[]>([]); // The tiles to place in the grid
    const [firstPass, setFirstPass] = useState(false);

    const updateDataTiles = (newStatusData: StatusHiveSiteData[], newDimensions: IHiveDimensions, dispatchMode: boolean) => {
        const newGrid = new HexGridData(newDimensions.grid.gridWidth, newDimensions.grid.gridHeight);
        setGrid(newGrid);

        const result = generateDataTiles(newStatusData, newGrid, dispatchMode ? HiveGenerationMode.Dispatch : HiveGenerationMode.Clusters, true);
        setDataTiles(result.placed);

        if (result.failed.length > 0) {
            popToast(`Unable to place ${result.failed.length} tiles`, "warning", 2500);
        }

        setFirstPass(true);
    };

    useEffect(() => { // When window resizes, update all dimensions values
        const updateDimension = () => {
            const newDimensions = getCurrentDimension(gridConfig);
            setDimensions(newDimensions);
        }

        window.addEventListener('resize', updateDimension);

        return (() => {
            window.removeEventListener('resize', updateDimension);
        });
    }, [gridConfig]);

    useEffect(() => {
        setSiteData(statusList || []);
    }, [statusList]);

    useEffect(() => {
        const newData = processData(siteData || []);
        setData(newData);
    }, [siteData])

    useEffect(() => { // If data or dimensions change, update tiles
        updateDataTiles(data, dimensions, dispatchMode);
    }, [data, dimensions, dispatchMode]);

    useEffect(() => {
        if (dispatchMode) {
            setTimeout(() => {
            setDispatchMode(false)
            }, 180000)
        }
    }, [dispatchMode])

    const { tileRadius } = dimensions.grid;
    const { centre, topLeft, bottomLeft } = grid.getAnchorPoints();

    const updateData = (newData: ISiteStatus) => {
        const index = siteData.findIndex(siteStatus => siteStatus.siteId === newData.siteId)
        if (index > -1 && index < siteData.length) {
            setSiteData((previousData) => {
                let newStatusList = [...previousData]
                newStatusList[index] = newData
                return newStatusList
            })
        }
    }


    const flashingBorderIntervalMS = 1000;

    // Keeps flashing tiles in sync with on another
    useEffect(() => {
        const interval = setInterval(() => {
            setFlashSync(!flashSync)
        }, flashingBorderIntervalMS);

        return () => clearInterval(interval);
    }, [flashSync])

    const dispatchPerms: Partial<UserPermissionCollection> = {
        Asset: AssetPermissions.Admin
    }

    useEffect(() => {
        setDispatchModeAuthenitcated(rawAttributeData !== undefined && currentUser !== undefined && currentUser.hasPermissions(dispatchPerms) && currentUser.userName.toLowerCase() !== "emc@conradcloud.net")
    }, [currentUser, rawAttributeData])

    const loadCancelOutagesModal = async () => {
        const confirmationComponent = (
            <Typography>You are about to cancel all manual instructions and revert all sites to their PN plans.<br /> Are you sure you wish to continue?</Typography>
        );

        const options: IAdvancedModalOptions = {
            title: "Cancel Manual Dispatches",
            contentComponent: confirmationComponent,
            yesText: "Yes",
            noText: "Cancel",
            fullWidth: true,
            maxWidth: 'sm',
            canSubmit: true,
            error: false
        }

        return await openModal(options);
    };

    const GetSiteManualControlWebID = (siteName: string, rawSiteAttributeValues: IMetadataAttributeValue[]) => {
        let webID = ""
        rawSiteAttributeValues.forEach(attribute => {
            if (attribute.siteName === siteName && attribute.attributeName === "siteManualControl") {
                webID = attribute.webID
            }
        })
        return webID
    }

    const GetAllManualSiteNames = () => {
        let manualSites: string[] = []
        siteData.forEach(site => {
            if (site.manualStart || site.manualStop) {
                manualSites.push(site.siteName)
            }
        })
        return manualSites
    }

    const dispatchSite = async (request: IDispatchRequest) => {
        if (rawAttributeData) {
            const webID = GetSiteManualControlWebID(request.siteName, rawAttributeData)
            if (webID !== "") {
                popToast("Attempting to send dispatch signal to " + request.siteName)
                const response = await piApplicationDispatchApi.sendManualDispatchRequest(request.siteName, webID, request.newManualStatus)
                if (response.status) {
                    popToast("Dispatch signal to " + request.siteName + " sent", "success")
                }
                else {
                    popToast("Failed to dispatch " + request.siteName, "error")
                }
                return response.status
            }
        }
        return false
    }

    const PopToastForCancelAllDispatches = (failedToCancelSites: string[], cancelledSites: string[]) => {
        if (failedToCancelSites.length > 0) {
            let toastContent = "Failed to cancel manual dispatch for: "
            failedToCancelSites.sort().forEach(siteName => {
                toastContent = toastContent + siteName + ", "
            })
            toastContent = toastContent.slice(0, -2);
            popToast(toastContent, "error")
        }
        else if (cancelledSites.length > 0) {
            let toastContent = "Cancelled manual dispatch for: "
            cancelledSites.sort().forEach(siteName => {
                toastContent = toastContent + siteName + ", "
            })

            toastContent = toastContent.slice(0, -2);
            popToast(toastContent, "success")
        }
    }
    
    const updateTilesFromCancelAll = (cancelledSites: string[]) => {
        if (cancelledSites.length > 0) {
            setSiteData((previousData) => {
                let newStatusList: ISiteStatus[] = []
                previousData.forEach(siteStatus => {
                    let newStatus = { ...siteStatus }
                    if (cancelledSites.indexOf(siteStatus.siteName) >= 0) {
                        newStatus.manualStart = false
                        newStatus.manualStop = false
                        newStatus.siteManualControl = 0
                        newStatus.siteRunningStatus = 0
                    }
                    newStatusList.push(newStatus)
                })
                return newStatusList
            })
        }
    }

    const cancelAllManualDispatches = async () => {
        const shouldCancelDispatches = await loadCancelOutagesModal()
        if (shouldCancelDispatches && rawAttributeData) {
            const manualSites = GetAllManualSiteNames()
            const promises: Promise<IDispatchResponse>[] = []
            manualSites.forEach(siteName => {
                const webID = GetSiteManualControlWebID(siteName, rawAttributeData)
                promises.push(piApplicationDispatchApi.sendManualDispatchRequest(siteName, webID, SiteManualStatus.NotManual))
            })
            popToast("Cancelling dispatches for " + promises.length + " sites")
            const result = await Promise.all(promises)
            const cancelledSites: string[] = []
            const failedToCancelSites: string[] = []
            result.forEach(attemptedDispatch => {
                if (attemptedDispatch.status) {
                    cancelledSites.push(attemptedDispatch.siteName)
                }
                else {
                    failedToCancelSites.push(attemptedDispatch.siteName)
                }
            })
            updateTilesFromCancelAll(cancelledSites)
            PopToastForCancelAllDispatches(failedToCancelSites, cancelledSites)
            setDispatchMode(false)
        }
    }

    if (!firstPass) { // Gives the grid an opportunity to have calculated at least once before displaying anything
        return (<></>)
    }

    return (
        <HexContainer dimensions={dimensions}>
            {disptachModeAuthenticated && <DispatchButton location={topLeft} dispatchActive={dispatchMode} setDispatchActive={setDispatchMode} />}
            {dispatchMode && <CancelDispatchButton location={bottomLeft} onClick={cancelAllManualDispatches} />}
            {
                loaded ?
                    dataTiles.map((tile) => tile.graphic(dispatchMode, updateData, flashSync, dispatchSite))
                :
                    GetUKLayout(centre).map((coords, index) => (
                        <Hexagon key={`load-${index}`} q={coords.q} r={coords.r} s={coords.s} fill="none" stroke={isDarkMode ? common.white : common.black} strokeWidth={"0.01em"} />
                    ))
            }

            {!dispatchMode &&
                <Hexagon q={centre.q} r={centre.r} s={centre.s} fill="none" stroke={isDarkMode ? common.white : common.black} strokeWidth={"0.01em"}>
                    <image className="pulsing" x={-(tileRadius / 2.5)} y={-(tileRadius / 2)} href={isDarkMode ? ionLogoWhite : ionLogoBlack} height={tileRadius} width={tileRadius}></image>
                </Hexagon>
            }
        </HexContainer>
    );
}

const StatusHivePageConfig: IPortalRouteOptions = {
    relativeUrl: "status-hive",
        navDisplay: {
        title: "Status Hive",
        icon: <Hive />
    },
    page: {
        element: <StatusHivePage />,
        disableGutters: true,
        forceOverlayNav: true,
    },
    hideOnMobile: true
}

export default StatusHivePageConfig;
